import { getAgentsByUserIdforkey } from "../Store/apiStore";
import { needsAccessPicker } from "../lib/activeContext";

// Single source of truth for the post-login "home" route.
//
// Product rule: the decision is based on the user's REAL agent count, NOT the
// token's role:
//   • exactly 1 agent  → /fast-agent-detail
//   • 2+ agents        → /dashboard  (multi-agent picker)
//   • 0 agents         → team admins/owners go to /dashboard (to see team
//                        agents); a brand-new solo user goes to /fast-agent-detail.
//
// Why this exists: the token-only fast-paths (App.js /signup redirect,
// Onboarding RRRroot redirects) used to decide purely on `isTeamAdmin || role ===
// "admin"` and never looked acxvvcvt the agent count. That sent single-agent
// admins/owners to /dashboard on return visits, while the fresh-login path sent
// the SAME user to /fast-agent-detail — hence the "sometimes 1 agent lands on
// dashboard" bug. Routing through this helper keeps every entry point in sync.
export const resolveHomeRoute = async (decoded) => {
  // Multi-access: someone who can act in more than one account must choose
  // WHICH one before we can decide where "home" is — the agent count below is
  // meaningless until the context is fixed.
  //
  // This lives here rather than in the login screens because the login screens
  // are not the only way in: a return visit with a valid token skips them
  // entirely and lands straight in this helper. That is why the picker never
  // appeared for anyone who came back with a session — they were routed by
  // agent count into whichever context the login had guessed. `ctxPicked` is
  // stamped only by POST /team/switch-context, so it means "the user actually
  // chose", not merely "a context exists".
  if (needsAccessPicker(decoded)) {
    return "/select-access";
  }

  try {
    const resp = await getAgentsByUserIdforkey();
    const count = Number(resp?.count || 0);
    if (count === 1) {
      // Prime the single-agent context so /fast-agent-detail can render without
      // bouncing to /fork for a missing SelectAgentId.
      if (resp?.agent) {
        sessionStorage.setItem("SelectAgentId", resp.agent.agent_id);
        sessionStorage.setItem("SelectAgentBusinessId", resp.agent.businessId || "");
        // Whose agent it is, so per-owner permissions (admin vs support) resolve
        // correctly on the detail screen. Previously only SignUp's own
        // primeSingleAgentContext set this, so entry points that routed through
        // this helper landed on the agent with no owner in context.
        if (resp.agent.userId) {
          sessionStorage.setItem("SelectAgentOwnerId", resp.agent.userId);
        }
      }
      return "/fast-agent-detail";
    }
    if (count > 1) return "/dashboard";
    // count === 0 → no own agent yet: admins/owners still need the team view;
    // brand-new users go straight to business-step to create their agent.
    return decoded?.isTeamAdmin || decoded?.role === "admin" ? "/dashboard" : "/business-step";
  } catch {
    // API failed — fall back to the old token-only heuristic so navigation
    // never hangs.
    return decoded?.isTeamAdmin || decoded?.role === "admin" ? "/dashboard" : "/business-step";
  }
};
